/* global globalThis */
'use strict';

/**
 * Webduct Permissions Helper
 *
 * Library for checking BCX roles and permissions.
 *
 * @version
 * 3.12.0 LIBENV-276 Remove classic manager methods and related code
 * 3.11.0 LIBENV-273 Add missing resource links for fittings and products
 * 3.9.0 LIBENV-270 Allow Product Admin, Deployment Admin to view Application Settings
 * 3.8.0 LIBENV-268 Adjust permissions for viewing Manufacturing Admin
 * 3.7.0 LIBENV-266 Update methods for application settings URL & permissions for manufacturing-admin-ui
 * 3.6.0 LIBENV-263 Allow Order User to add messages on order details
 * 3.5.0 LIBENV-260 Add permission for viewing application settings page in manufacturing-admin-ui
 * 3.5.0 LIBENV-261 Add getSpoolingUIUrl() and permission canViewSpoolingUI
 * 3.4.0 LIBENV-257 Add vehicle assignment permissions
 * 3.4.0 LIBENV-206 Add vehicle management permissions
 * 3.2.0 LIBENV-251 Remove fittingAdmin role check in canManageExportScripts
 * 3.1.0 LIBENV-247 Default all users to the landing page after login
 * 2.55.0 LIBENV-237 Allow central product users to access the central product UI
 * 2.54.0 LIBENV-231 Add canViewSeatUsers permission
 * 2.54.0 LIBENV-233 Changes required for landing page
 * 2.50.0 LIBENV-225 Add permissions for managing shipping loading zones
 * 2.48.0 LIBENV-221 Add canViewAdjustments Permission
 * 2.46.0 LIBENV-216 Change locked timecard permissions
 * 2.45.0 LIBENV-213 Add new role and methods for: CentralProductUser
 * 2.40.0 LIBENV-198 Add checks to not allow FieldOrderAdmin role to update orders
 * 2.36.0 LIBENV-186 Add permission for viewing activity report
 * 2.35.0 LIBENV-184 Add getProductApiPath() and permission/url for Product UI
 * 2.32.0 LIBENV-171 Add permissions and urls for CAM export scripts
 * 2.31.1 LIBENV-174 Add ecommerce user check for viewing organizations
 * 2.31.0 LIBENV-172 Add ecommerce user check for viewing the pricing adjustments
 * 2.30.0 LIBENV-169 Add permissions and urls for CAM export item files and version
 * 2.29.0 LIBENV-167 Add permissions for CAM export mappings
 * 2.28.0 LIBENV-165 Add manufacturing-admin-ui resource URL and permissions
 * 2.27.0 LIBENV-162 Add joint library management permission
 * 2.26.0 LIBENV-160 Add order management permissions
 * 2.24.0 LIBENV-154 Add joints routing and permissions
 * 2.23.0 LIBENV-150 Shop Report: add permission checks
 * 2.23.0 LIBENV-149 Supply Only Report: add permission checks
 * 2.23.0 LIBENV-142 Billing Report: support new urls
 * 2.20.0 LIBENV-143 Add On Screen Assembly User permission
 * 2.19.0 LIBENV-140 Add canViewApplicationSettingsAdmin Permission
 * 2.19.0 LIBENV-139 Add Payroll Admin Permission
 * 2.15.0 LIBENV-133 Add View/Manage Price Adjustment Permissions
 * 2.14.0 LIBENV-129 Add View/Manage Organizations Permissions
 * 2.12.0 LIBENV-126 Field admins should be able to do things only foremen are currently able to do
 * 2.11.0 LIBENV-124 Add timecard and reimbursement permissions for crew filters
 * 2.10.0 LIBENV-122 Allow field admins and foremen to access timecards overview reports
 * 2.8.0 LIBENV-188 Permission Tweaks for Header Links
 * 2.7.0 LIBENV-116 Allow Timecard and Reimbursement Users to View Their Own Crew
 * 2.6.0 LIBENV-114 Allow group admins and deployment admins to manage users
 * 2.5.0 LIBENV-111 Add Crew Permissions and Resources
 * 2.4.0 LIBENV-108 Add Reimbursement Foreman Permission
 * 2.3.0 LIBENV-106 Add Environment and User Group Permissions
 * 2.2.0 LIBENV-104 Add missing header permissions and resources
 * 1.28.0 LIBENV-90 Update billing detail report permission and resource
 * 1.20.0 LIBENV-74 Add new permission for managing users without PIN
 * 1.19.0 LIBENV-72 Add Permission to Edit Locked Reimbursements
 * 1.18.0 LIBENV-70 Add Attendance Permissions
 * 1.17.0 LIBENV-68 Add canEditLockedTimecards Permission
 * 1.15.0 LIBENV-53 Add WebductPermissions into globalThis for ES6 usage
 * 1.14.0 LIBENV-63 Add base path to access timecards
 * 1.14.0 LIBENV-62 Add Reimbursement Permissions
 * 1.13.0 LIBENV-60 Add Reimbursement UI resource with basic permission
 * 1.12.0 LIBENV-56 Update permissions for editing submitted Timecards
 * 1.11.0 LIBENV-57 Add permission for vendor sync button
 * 1.10.0 LIBENV-54 Update permissions for editing submitted Timecards
 * 1.4.6 LIBENV-39 Allow users with `change fulfillment group status` permission to be able to change the delivery status
 * 1.4.3 LIBENV-33 Update  canAddOrderMessages() to exclude eCommerce users
 * 1.4.2 LIBENV-31 Update viewing Dashboard Order permission to return false for Ecommerce Users
 * 1.3.2 LIBENV-23 Add Dashboard Permissions
 * 1.1.5 LIBENV-14 Allow field users to see production and delivery calendar
 * 1.1.3 LIBENV-10 Order archive listing not displaying Fab or Remaining Fab time
 * 1.1.2 LIBENV-8 Fix Broken Resources
 * 1.0.0 LIBENV-1 Initialize environment library
 *
 * @author Erik Orozco <eorozco@intersog.com>
 * @author Ryan Sweeney <rsweeney@buildcentrix.com>
 * @author Jenny Lam <jlam@buildcentrix.com>
 * @author Luis Hernandez <lhernandez@intersog.com>
 * @author Dallas Vogels <dallas.vogels@buildcentrix.com>
 * @author Deitric Jang <djang@buildcentrix.com>
 * @author Monika Gole <monika.gole@ncircletech.com>
 *
 * @copyright (c) 2019-2024 Build Centrix
 *
 */
var WebductPermissions = function(user) {

    /**
     * Webduct Permissions
     *
     * This object is returned.
     *
     * @since 1.0.0
     *
     * @type {Object}
     */
    var helper = {};

    // Permissions Initialization
    var CENTRAL_PRODUCT_USER;
    var CREW_USER;
    var CREW_ADMIN;
    var CUSTOMER;
    var USER_ADMIN;
    var PRODUCT_ADMIN;
    var VIEW_PRICING;
    var VIEW_WEIGHT;
    var FITTING_ADMIN;
    var ORDER_ADMIN;
    var DEPLOYMENT_ADMIN;
    var DIVISION_ADMIN;
    var SUPER_ADMIN;
    var DEVELOPER;
    var JOB_ADMIN;
    var TRACKER_USER;
    var TRACKER_ADMIN;
    var FIELD_ORDER_ADMIN;
    var FIELD_TIMECARD_ADMIN;
    var FIELD_TIMECARD_FOREMAN;
    var FIELD_TIMECARD_WORKSTATION_USER;
    var REIMBURSEMENT_ADMIN;
    var REIMBURSEMENT_FOREMAN;
    var REIMBURSEMENT_USER;
    var SHOP_TIMECARD_WORKSTATION_USER;
    var SHOP_TIMECARD_ADMIN;
    var SHOP_TIMECARD_VIEW_PRODUCTION_CALENDAR;
    var SHOP_TIMECARD_VIEW_DELIVERY_CALENDAR;
    var SHOP_TIMECARD_CHANGE_FULFILLMENT_STATUS;
    var LOOKAHEAD_ORDER_ADMIN;
    var TIMECARD_PAYROLL_ADMIN;
    var ON_SCREEN_ASSEMBLY_USER;

    /************************************ Old Functions (Deprecated) **************************************************/

    /**
     * User can CHANGE the fulfillment group status of an order
     *
     * @deprecated Use `canChangeFulfillmentGroupStatus`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canChangeOrderFulfillmentGroupStatus = function() {
        return isChangeFulfillmentStatusUser();
    };

    /**
     * User IS Lookahead Order User
     *
     * @deprecated ``
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isLookaheadOrderAdmin = function () {
        return isLookaheadOrderAdminUser();
    };

    /**
     * User IS Shop timecard Administrator
     *
     * @deprecated ``
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isShopTimeCardAdmin = function () {
        return isShopTimecardAdmin();
    };

    /**
     * Field Timecard Foreman
     *
     * @deprecated ``
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isFieldTimeCardForeman = function () {
        return isFieldTimecardForeman();
    };

    /**
     * User IS Shop timecard workstation user
     *
     * @deprecated ``
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isShopTimeCardWorkstationUser = function () {
        return isShopTimecardUser();
    };

    /**
     * User IS Tracker Administrator
     *
     * @deprecated ``
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isTrackerAdmin = function () {
        return isTrackerAdminUser();
    };

    /**
     * Tracker User
     *
     * @deprecated ``
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isTrackerUser = function () {
        return isTrackerUser();
    };

    /**
     * View Weight
     *
     * @deprecated Use `canViewWeight`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.viewWeight = function () {
        return isViewWeightUser();
    };

    /**
     * View Pricing
     *
     * @deprecated Use `canViewPricing`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.viewPricing = function () {
        return isViewPricingUser();
    };

    /**
     * View Fabrication Time
     *
     * The system was developed where viewing pricing also dictated if a user could see fabrication time and weight,
     * thus this function uses the VIEW_WEIGHT permission to determine the result.
     *
     * @deprecated Use `canViewFabricationTime`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.viewFabricationTime = function () {
        return isViewFabricationTimeUser();
    };

    /**
     * Field timecard workstation user
     *
     * @deprecated Use `isFieldTimecardUser`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isFieldTimeCardWorkstationUser = function () {
        return isFieldTimecardUser();
    };

    /**
     * Check for time-card user permission
     *
     * @deprecated Use `isFieldTimecardUser`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isTimeCardUser = function() {
        return isTimecardUser();
    };

    /**
     * Field timecard Administrator
     *
     * @deprecated Use `isFieldTimecardAdmin`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isFieldTimeCardAdmin = function () {
        return isFieldTimecardAdmin();
    };

    /**
     * Check for time-card admin permission
     *
     * @deprecated Use `isTimecardAdmin`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isTimeCardAdmin = function() {
        return isTimecardAdmin();
    };

    /**
     * Check if user is an order administrator
     *
     * @deprecated Use `isOrderAdminUser`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isOrderAdmin = function() {
        return isOrderAdminUser();
    };

    /**
     * Check if user is in an internal group
     *
     * @deprecated Use `isInternalUser`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isInternal = function() {
        return isInternalUser();
    };

    /**
     * Check if user is in an e-commerce group
     *
     * @deprecated Use `isEcommerceUser`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isEcommerce = function() {
        return isEcommerceUser();
    };

    /**
     * Check if user is a deployment administrator
     *
     * @deprecated Use `isDeploymentAdminUser`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isDeploymentAdmin = function() {
        return isDeploymentAdminUser();
    };

    /**
     * Check if user is an order admin in an internal division OR a deployment admin
     *
     * @deprecated Use `isInternalOrderAdminDeploymentUser`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isOrderAdminDeployment = function() {
        return isInternalOrderAdminDeploymentUser();
    };

    /**
     * Check for export admin
     * Wrapper for isOrderAdmin().
     *
     * @deprecated Use ``
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isExportAdmin = function() {
        return isOrderAdminUser();
    };

    /**
     * Check if the user's group (company/division) has the shop standards flag enabled
     *
     * @deprecated Use `canUseShopStandards`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isShopStandardsEnabled = function() {
        return isShopStandardsUser();
    };

    /**
     * Check if the user is a Central Product User
     * @todo Check if this can be deprecated and replaced by canViewProductUI
     * @since 2.45.0
     * @returns {Boolean}
     */
    helper.isCentralProductUser = function() {
        return isCentralProductUser();
    };

    /**
     * Get the user's info from the `wd.settings` cookie.
     * This includes the permission and seat type index.
     *
     * @deprecated Use `user.getInformation`
     * @since 1.0.0
     * @returns {Object}
     */
    helper.getUserInfo = function() {
        return user.getInformation();
    };

    /**
     * Check if a user has a role in their stored helper
     *
     * @since 1.0.0
     * @deprecated Use `user.hasRole`
     * @param {Number} role
     * @return {Boolean}
     */
    helper.userHasRole = function(role) {
        return user.hasRole(role);
    };

    /**
     * User has permission
     *
     * Wrapper to user.hasPermission. Used to help separate two concepts that are architecturally one.
     *
     * @since 1.0.0
     * @deprecated Use `user.hasPermission`
     * @param {Number} permission
     * @return {Boolean}
     */
    helper.userHasPermission = function (permission) {
        return user.hasPermission(permission);
    };

    /**
     * Get the user's helper from storage
     *
     * @since 1.0.0
     * @deprecated Use `user.getPermissions`
     * @return {Object}
     */
    helper.getPermissionsFromStorage = function() {
        return user.getPermissions();
    };

    /**
     * Check if user is a developer
     *
     * @deprecated Use `isDeveloperUser`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isDeveloper = function() {
        return isDeveloperUser();
    };

    /**
     * Check if user is a super administrator
     *
     * @deprecated Use `isSuperAdminUser`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isSuperAdmin = function() {
        return isSuperAdminUser();
    };

    /**
     * Check if user is a user administrator
     *
     * @deprecated Use `isUserAdmin`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isUserAdmin = function() {
        return isUserAdmin();
    };

    /**
     * Check if user is a division administrator
     *
     * @deprecated Use `isDivisionAdmin`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isDivisionAdmin = function() {
        return isDivisionAdmin();
    };

    /**
     * Check if user is a job administrator
     *
     * @deprecated Use `isJobAdminUser`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isJobAdmin = function() {
        return isJobAdminUser();
    };

    /**
     * Check if user is a fitting administrator
     *
     * @deprecated Use `isFittingAdminUser`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isFittingAdmin = function() {
        return isFittingAdminUser();
    };

    /**
     * Check if user is a product administrator
     *
     * @deprecated Use `isProductAdminUser`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isProductAdmin = function() {
        return isProductAdminUser();
    };

    /**
     * Check if user is a customer
     *
     * @deprecated Use `isCustomerUser`
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.isCustomer = function() {
        return isCustomerUser();
    };

    /************************************** External Functionality ****************************************************/

    /**
     * User can ADD Crews
     *
     * @since 2.5.0
     * @returns {Boolean}
     */
    helper.canAddCrews = function() {
        return isCrewUser() || isCrewAdmin();
    };

    /**
     * User can ADD Field Timecard Entries
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canAddFieldTimecardEntry = function() {
        return isFieldTimecardForeman();
    };

    /**
     * User can ADD fittings
     *
     * @since 3.11.0
     * @returns {Boolean}
     */
    helper.canAddFittings = function() {
        return isFittingAdminUser() || isDeploymentAdminUser() || isSuperAdminUser();
    };

    /**
     * User can ADD Order Messages
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canAddOrderMessages = function() {
        return isCustomerUser() || canViewPriceOrWeight() || isDeploymentAdminUser() || isOrderAdminUser() || isSuperAdminUser();
    };

    /**
     * User can ADD OWN Reimbursements
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canAddOwnReimbursements = function() {
        return hasReimbursementPermissions();
    };

    /**
     * User can ADD ALL Reimbursements
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canAddAllReimbursements = function() {
        return isReimbursementAdmin() || isReimbursementForeman();
    };

    /**
     * User can ADD OWN Reimbursement Entries
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canAddOwnReimbursementEntries = function() {
        return hasReimbursementPermissions();
    };

    /**
     * User can ADD ALL Reimbursement Entries
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canAddAllReimbursementEntries = function() {
        return isReimbursementAdmin() || isReimbursementForeman();
    };

    /**
     * User can ADD product with attributes
     *
     * @since 3.11.0
     * @returns {Boolean}
     */
    helper.canAddProductWithAttributes = function() {
        return isProductAdminUser() || isDeploymentAdminUser() || isSuperAdminUser();
    };

    /**
     * User can ADD Shop Timecard Entries
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canAddShopTimecardEntry = function() {
        return isShopTimecardAdmin() || isReimbursementForeman();
    };

    /**
     * User can ADD simple product
     *
     * @since 3.11.0
     * @returns {Boolean}
     */
    helper.canAddSimpleProduct = function() {
        return isProductAdminUser() || isDeploymentAdminUser() || isSuperAdminUser();
    };

    /**
     * User can ADD Timecard Entries on Behalf of Others
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canAddTimecardOnBehalf = function() {
        return isTimecardAdmin();
    };

    /**
     * User can APPROVE Reimbursements
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canApproveReimbursements = function() {
        return isReimbursementAdmin() || isReimbursementForeman();
    };

    /**
     * User can APPROVE Timecards
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canApproveTimecard = function() {
        return isTimecardAdmin();
    };

    /**
     * User can ARCHIVE orders
     *
     * @since 1.0.0
     */
    helper.canArchiveOrders = function () {
        return isInternalOrderAdminDeploymentUser();
    };

    /**
     * User can ASSIGN Inventory
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canAssignInventory = function() {
        return isTrackerAdminUser();
    };

    /**
     * User can BYPASS PIN AUTHENTICATION for other users
     *
     * @since 1.20.0
     * @returns {Boolean}
     */
    helper.canBypassUserPinAuthentication = function() {
        return isShopTimecardAdmin() || isFieldTimecardAdmin();
    };

    /**
     * User can CHANGE Delivery Date
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canChangeDeliveryDate = function() {
        return (canUpdateOrders() && (isInternalOrderAdmin() || isLookaheadOrderAdminUser())) || isSuperAdminUser();
    };

    /**
     * User can CHANGE Delivery Status
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canChangeDeliveryStatus = function() {
        return (canUpdateOrders() && (isInternalOrderAdmin() || isLookaheadOrderAdminUser() || isChangeFulfillmentStatusUser())) || isSuperAdminUser();
    };

    /**
     * User can CHANGE Fulfillment Group Status
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canChangeFulfillmentGroupStatus = function() {
        return (canUpdateOrders() && (isInternalOrderAdmin() || isLookaheadOrderAdminUser() || isChangeFulfillmentStatusUser())) || isSuperAdminUser();
    };

    /**
     * Can change the delivery date of an order
     *
     * @since 1.0.0
     *
     * @returns {Boolean}
     */
    helper.canChangeOrderDeliveryDate = function() {
        return isInternalOrderAdminDeploymentUser();
    };

    /**
     * User can CHANGE the production date of an order
     *
     * @since 1.0.0
     *
     * @returns {Boolean}
     */
    helper.canChangeOrderProductionDate = function() {
        return isInternalOrderAdminDeploymentUser();
    };

    /**
     * User can CHANGE Order Status
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canChangeOrderStatus = function() {
        return (canUpdateOrders() && (isInternalOrderAdmin() || isLookaheadOrderAdminUser())) || isSuperAdminUser();
    };

    /**
     * User can CHANGE Production Date
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canChangeProductionDate = function() {
        return (canUpdateOrders() && (isInternalOrderAdmin() || isLookaheadOrderAdminUser())) || isSuperAdminUser();
    };

    /**
     * User can CHANGE Production Status
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canChangeProductionStatus = function() {
        return false;
    };

    /**
     * User can CHANGE Reimbursement Statuses
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canChangeReimbursementStatus = function() {
        return isReimbursementAdmin() || isReimbursementForeman();
    };

    /**
     * User can CHANGE Timecard Time Increment Settings
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canChangeTimeIncrementSettings = function() {
        return isShopTimecardAdmin();
    };

    /**
     * User can CHANGE Timecard Entry Pay Rate Type
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canChangeTimecardEntryPayRate = function() {
        return isTimecardAdmin();
    };

    /**
     * User can CHANGE Timecard Entry User Group
     * @returns {Boolean}
     */
    helper.canChangeTimecardEntryUserGroup = function() {
        return isTimecardAdmin();
    };

    /**
     * User can CHANGE Timecard Shift Type
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canChangeTimecardShiftType = function() {
        return isTimecardAdmin();
    };

    /**
     * User can CHANGE Timecard Status
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canChangeTimecardStatus = function() {
        return isTimecardAdmin();
    };

    /**
     * User can CHANGE Tracking Date
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canChangeTrackingDate = function() {
        return isTrackerAdminUser();
    };

    /**
     * User can CLONE Timecards
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canCloneTimecard = function() {
        return isTimecardAdmin();
    };

    /**
     * User can DELETE Order Messages
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canDeleteOrderMessages = function() {
        return (canUpdateOrders() && isInternalOrderAdmin()) || isDeploymentAdminUser() || isSuperAdminUser();
    };

    /**
     * User can DELETE OWN Crews
     *
     * @since 2.5.0
     * @returns {Boolean}
     */
    helper.canDeleteOwnCrews = function() {
        return isCrewUser();
    };

    /**
     * User can DELETE OWN Reimbursements
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canDeleteOwnReimbursements = function() {
        return hasReimbursementPermissions();
    };

    /**
     * User can DELETE ALL Crews
     *
     * @since 2.5.0
     * @returns {Boolean}
     */
    helper.canDeleteAllCrews = function() {
        return isCrewUser() || isCrewAdmin();
    };

    /**
     * User can DELETE ALL Reimbursements
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canDeleteAllReimbursements = function() {
        return isReimbursementAdmin() || isReimbursementForeman();
    };

    /**
     * User can DELETE OWN Reimbursement Entries
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canDeleteOwnReimbursementEntries = function() {
        return hasReimbursementPermissions();
    };

    /**
     * User can DELETE ALL Reimbursement Entries
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canDeleteAllReimbursementEntries = function() {
        return isReimbursementAdmin() || isReimbursementForeman();
    };

    /**
     * User can DELETE Timecards
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canDeleteTimecard = function() {
        return isTimecardAdmin();
    };

    /**
     * User can EDIT Category
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canEditCategories = function () {
        return isProductAdminUser();
    };

    /**
     * User can EDIT E-commerce Job
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canEditEcommerceJob = function() {
        return (canUpdateOrders() && isInternalOrderAdmin()) || isSuperAdminUser();
    };

    /**
     * User can EDIT Product
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canEditProducts = function () {
        return isProductAdminUser();
    };

    /**
     * User can EDIT OWN Crews
     *
     * @since 2.5.0
     * @returns {Boolean}
     */
    helper.canEditOwnCrews = function() {
        return isCrewUser();
    };

    /**
     * User can EDIT OWN Reimbursements
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canEditOwnReimbursements = function() {
        return hasReimbursementPermissions();
    };

    /**
     * User can EDIT ALL Crews
     *
     * @since 2.5.0
     * @returns {Boolean}
     */
    helper.canEditAllCrews = function() {
        return isCrewUser() || isCrewAdmin();
    };

    /**
     * User can EDIT ALL Reimbursements
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canEditAllReimbursements = function() {
        return isReimbursementAdmin() || isReimbursementForeman();
    };

    /**
     * User can EDIT OWN Reimbursement Entries
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canEditOwnReimbursementEntries = function() {
        return hasReimbursementPermissions();
    };

    /**
     * User can EDIT ALL Reimbursement Entries
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canEditAllReimbursementEntries = function() {
        return isReimbursementAdmin() || isReimbursementForeman();
    };

    /**
     * User can EDIT LOCKED Reimbursements
     *
     * @since 1.19.0
     * @returns {Boolean}
     */
    helper.canEditLockedReimbursements = function() {
        return isReimbursementAdmin() || isReimbursementForeman();
    };

    /**
     * User can EDIT LOCKED Timecards
     *
     * @since 2.46.0
     * @returns {Boolean}
     */
    helper.canEditLockedTimecards = function() {
        return isTimecardPayrollAdmin();
    };

    /**
     * User can EDIT Order Dates
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canEditOrderDates = function() {
        return (canUpdateOrders() && (isInternalOrderAdmin() || isLookaheadOrderAdminUser())) || isSuperAdminUser();
    };

    /**
     * User can EDIT Order Details
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canEditOrderDetails = function() {
        return (canUpdateOrders() && (isInternalOrderAdmin() || isLookaheadOrderAdminUser())) || isSuperAdminUser();
    };

    /**
     * User can EDIT Order Delivery Details
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canEditOrderDeliveryDetails = function() {
        return (canUpdateOrders() && (isInternalOrderAdmin() || isLookaheadOrderAdminUser())) || isSuperAdminUser();
    };

    /**
     * User can EDIT Order Messages
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canEditOrderMessages = function() {
        return (canUpdateOrders() && isInternalOrderAdmin()) || isDeploymentAdminUser() || isSuperAdminUser();
    };

    /**
     * User can EDIT Submitted Timecard Entries
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canEditSubmittedTimecardEntry = function() {
        return isShopTimecardAdmin() || isFieldTimecardAdmin();
    };

    /**
     * User can EDIT Attendance
     *
     * @since 1.18.0
     * @returns {Boolean}
     */
    helper.canEditTimecardAttendance = function() {
        return isTimecardUser();
    };

    /**
     * User can EXPORT to CSV
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canExportToCSV = function() {
        return isTrackerAdminUser();
    };

    /**
     * User can FILTER Jobs
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canFilterJobs = function() {
        return isTrackerAdminUser();
    };

    /**
     * User can FILTER Users
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canFilterUsers = function() {
        return isTrackerAdminUser();
    };

    /**
     * User can FILTER Timecards Users
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canFilterTimecardUsers = function() {
        return isShopTimecardAdmin();
    };

    /**
     * User can FILTER Timecards Users by Crew
     * @deprecated Use "canViewCrews" instead
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canFilterTimecardUsersByCrew = function() {
        return isShopTimecardAdmin() || isFieldTimecardAdmin();
    };

    /**
     * User can FILTER Timecard User Groups
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canFilterTimecardUserGroups = function() {
        return isShopTimecardAdmin();
    };

    /**
     * User can MANAGE Adjustments
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canManageAdjustments = function() {
        return (canUpdateOrders() && (isInternalOrderAdmin() || isLookaheadOrderAdminUser())) || isSuperAdminUser();
    };

    /**
     * User can MANAGE Assemblies
     *
     * @since 2.20.0
     * @returns {Boolean}
     */
    helper.canManageAssemblies = function() {
        return isOnScreenAssemblyUser();
    };

    /**
     * User can MANAGE CAM Export item files
     *
     * @since 2.30.0
     * @returns {Boolean}
     */
    helper.canManageCamExportItemFiles = function () {
        return isDeploymentAdminUser();
    };

    /**
     * User can MANAGE CAM Export mappings
     *
     * @since 2.29.0
     * @returns {Boolean}
     */
    helper.canManageCamExportMappings = function () {
        return isDeploymentAdminUser();
    };

    /**
     * User can MANAGE CAM Export scripts
     *
     * @since 2.32.0
     * @returns {Boolean}
     */
    helper.canManageCamExportScripts = function () {
        return isSuperAdminUser();
    };

    /**
     * User can MANAGE CAM export software versions
     *
     * @since 2.30.0
     * @returns {Boolean}
     */
    helper.canManageCamExportSoftwareVersions = function () {
        return isDeploymentAdminUser();
    };

    /**
     * User can MANAGE Inventory Types
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canManageInventoryTypes = function() {
        return isTrackerAdminUser();
    };

    /**
     * User can MANAGE Joints
     *
     * @since 2.24.0
     * @returns {Boolean}
     */
    helper.canManageJoints = function () {
        return isProductAdminUser();
    };

    /**
     * User can MANAGE Joint Library Details
     *
     * @since 2.27.0
     * @returns {Boolean}
     */
    helper.canManageJointLibraryDetails = function () {
        return isSuperAdminUser();
    };


    /**
     * User can MANAGE Orders
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canManageOrders = function() {
        return (canUpdateOrders() && isTrackerAdminUser()) || isSuperAdminUser();
    };

    /**
     * User can MANAGE Organizations
     *
     * @since 2.14.0
     * @return {Boolean}
     */
    helper.canManageOrganizations = function() {
        return isDivisionAdmin();
    };

    /**
     * User can MANAGE Price Adjustments
     *
     * @since 2.15.0
     * @return {Boolean}
     */
    helper.canManagePriceAdjustments = function() {
        return isDivisionAdmin();
    };

    /**
     * User can MANAGE Timecard Settings
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canManageSettings = function() {
        return isShopTimecardAdmin() || isFieldTimecardAdmin();
    };

    /**
     * User can MANAGE Shipping Loading Zones
     *
     * @since 2.50.0
     * @returns {Boolean}
     */
    helper.canManageShippingLoadingZones = function() {
        return isOrderAdminUser() || isSuperAdminUser();
    };

    /**
     * User can MANAGE Shipping Vehicles
     *
     * @since 3.4.0
     * @returns {Boolean}
     */
    helper.canManageShippingVehicles = function() {
        return isOrderAdminUser() || isSuperAdminUser();
    };

    /**
     * User can MANAGE Timecard Payroll
     *
     * @since 2.19.0
     * @returns {Boolean}
     */
    helper.canManageTimecardPayroll = function() {
        return isTimecardPayrollAdmin();
    };

    /**
     * User can MANAGE vehicle assignments
     *
     * @since 3.4.0
     * @returns {Boolean}
     */
    helper.canManageVehicleAssignments = function() {
        return isOrderAdminUser();
    };


    /**
     * User can MANAGE Work Orders
     *
     * @since 2.26.0
     * @returns {Boolean}
     */
    helper.canManageWorkOrders = function() {
        return isOrderUser() || isOrderAdminUser();
    };

    /**
     * User can PRINT Timecards
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canPrintTimecard = function() {
        return isShopTimecardAdmin();
    };

    /**
     * User can SUBMIT Order On Behalf Of
     * Only order admins in the internal company can submit orders
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canSubmitOrdersOnBehalf = function () {
        return isInternalOrderAdmin() || isInternalLookaheadOrderAdminUser();
    };

    /**
     * User can SUBMIT Reimbursements
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canSubmitReimbursements = function() {
        return isReimbursementUser();
    };

    /**
     * User can SUBMIT Timecards
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canSubmitTimecard = function() {
        return isTimecardUser();
    };

    /**
     * User can SYNC to Vendor
     *
     * @since 1.11.0
     * @returns {Boolean}
     */
    helper.canSyncToVendor = function() {
        return isFieldTimecardAdmin();
    };

    /**
     * User can TOGGLE Between Calendar Types
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canToggleCalendars = function () {
        return isDeliveryCalendarUser() && isProductionCalendarUser();
    };

    /**
     * User can TOGGLE Message Visibility
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canToggleMessageVisibility = function() {
        return isOrderAdminUser();
    };

    /**
     * User can VIEW Adjustments
     *
     * @since 2.48.0
     * @returns {Boolean}
     */
    helper.canViewAdjustments = function() {
        return this.canManageAdjustments() || (isViewPricingUser() && isViewWeightUser());
    };

    /**
     * User can VIEW All Timecards
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewAllTimecards = function() {
        return isTimecardAdmin();
    };

    /**
     * User can VIEW Assembly UI
     *
     * @deprecated To standardize naming conventions. Use "canViewAssembliesUI"
     * @since 2.20.0
     * @returns {Boolean}
     */
    helper.canViewAssemblies = function() {
        return isOnScreenAssemblyUser();
    };

    /**
     * User can VIEW Assembly UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewAssembliesUI = function() {
        return isOnScreenAssemblyUser();
    };

    /**
     * User can VIEW Spooling UI
     *
     * @since 3.5.0
     * @returns {Boolean}
     */
    helper.canViewSpoolingUI = function() {
        return true;
    };

    /**
     * User can VIEW Billing Detail Report
     *
     * @deprecated To standardize naming conventions. Use "canViewInventoryBillingDetailReportUI"
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewBillingDetailReport = function () {
        return isTrackerUser();
    };

    /**
     * User can VIEW Calendar UI
     *
     * @deprecated To standardize naming conventions. Use "canViewCalendarUI"
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewCalendar = function() {
        return isDeliveryCalendarUser() || isProductionCalendarUser();
    };

    /**
     * User can VIEW Calendar UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewCalendarUI = function() {
        return isDeliveryCalendarUser() || isProductionCalendarUser();
    };

    /**
     * User can VIEW Product Catalogs
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewCatalogs = function () {
        return isDeploymentAdminUser() && isProductAdminUser();
    };

    /**
     * User can VIEW CAM export item files
     *
     * @since 2.30.0
     * @returns {Boolean}
     */
    helper.canViewCamExportItemFiles = function () {
        return isDeploymentAdminUser();
    };

    /**
     * User can VIEW CAM export mappings
     *
     * @since 2.29.0
     * @returns {Boolean}
     */
    helper.canViewCamExportMappings = function () {
        return isDeploymentAdminUser();
    };

    /**
     * User can VIEW CAM export software versions
     *
     * @since 2.30.0
     * @returns {Boolean}
     */
    helper.canViewCamExportSoftwareVersions = function () {
        return isDeploymentAdminUser();
    };

    /**
     * User can VIEW Camduct Export
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewCamductExport = function() {
        return isOrderAdminUser();
    };

    /**
     * User can VIEW Combined CSV Export
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewCombinedCsvExport = function() {
        return isInternalOrderAdmin() && isViewPricingUser() && isViewFabricationTimeUser();
    };

    /**
     * User can VIEW Crews
     *
     * @since 2.7.0
     * @returns {Boolean}
     */
    helper.canViewCrews = function() {
        return this.canViewAllCrews() || this.canViewOwnCrews();
    };

    /**
     * User can VIEW Crews and crew filtering in the timecard UI
     * Users with no crew permissions or that can only see their own timecards do not need to filter by crew
     *
     * @since 2.11.0
     * @returns {Boolean}
     */
    helper.canViewCrewFiltersForTimecards = function() {
        return this.canViewCrews() && (isTimecardAdmin());
    };

    /**
     * User can VIEW Crews and crew filtering in the reimbursement UI
     * Users with no crew permissions or that can only see their own reimbursements do not need to filter by crew
     *
     * @since 2.11.0
     * @returns {Boolean}
     */
    helper.canViewCrewFiltersForReimbursements = function() {
        return this.canViewCrews() && (isReimbursementForeman() || isReimbursementAdmin());
    };

    /**
     * User can VIEW Crews Admin
     *
     * @deprecated To standardize naming conventions. Use "canViewCrewsAdminUI"
     * @since 2.5.0
     * @returns {Boolean}
     */
    helper.canViewCrewsAdmin = function () {
        return isCrewUser() || isCrewAdmin();
    };

    /**
     * User can VIEW Crews Admin UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewCrewsAdminUI = function () {
        return isCrewUser() || isCrewAdmin();
    };

    /**
     * User can VIEW Dashboard
     *
     * @deprecated To standardize naming conventions. Use "canViewDashboardUI"
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewDashboard = function() {
        return isCustomerUser();
    };

    /**
     * User can VIEW Dashboard UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewDashboardUI = function() {
        return isCustomerUser();
    };

    /**
     * User can VIEW Dashboard Inventory
     *
     * @since 1.3.2
     * @returns {Boolean}
     */
    helper.canViewDashboardInventory = function() {
        return isDeploymentAdminUser() || isTrackerAdminUser() || isTrackerUser();
    };

    /**
     * User can VIEW Dashboard Orders
     *
     * @since 1.3.2
     * @returns {Boolean}
     */
    helper.canViewDashboardOrders = function() {
        return isDeploymentAdminUser() || (!isEcommerceUser() && isOrderAdminUser());
    };

    /**
     * User can VIEW Dashboard Timecards
     *
     * @since 1.3.2
     * @returns {Boolean}
     */
    helper.canViewDashboardTimecards = function() {
        return isDeploymentAdminUser() || isTimecardAdmin();
    };

    /**
     * User can VIEW Delivery Calendar
     *
     * @deprecated To standardize naming conventions. Use "canViewShippingCalendarUI"
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewDeliveryCalendar = function() {
        return isDeliveryCalendarUser();
    };

    /**
     * User can VIEW Fabrication Time on Delivery Calendar
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewDeliveryCalendarFabricationTime = function () {
        return false;
    };

    /**
     * User can VIEW Environments Admin
     *
     * @since 2.3.0
     * @returns {Boolean}
     */
    helper.canViewEnvironmentsAdmin = function () {
        return isEnvironmentAdmin();
    };

    /**
     * User can VIEW Fabrication Times
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewFabricationTime = function () {
        return isViewFabricationTimeUser();
    };

    /**
     * User can VIEW Field Foreman Admin
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewFieldForemanAdmin = function() {
        return isFieldTimecardForeman();
    };

    /**
     * User can VIEW Field PDF Export
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewFieldPdfExport = function() {
        return isOrderUser();
    };

    /**
     * User can VIEW Field Timecard Admin
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewFieldTimecardAdmin = function() {
        return isFieldTimecardAdmin();
    };

    /**
     * User can VIEW Field Timecard Workstation
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewFieldTimecardWorkstation = function() {
        return isFieldTimecardUser();
    };

    /**
     * User can VIEW Field Timecards UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewFieldTimecardsUI = function() {
        return isTimecardUser();
    };

    /**
     * User can VIEW Shop Timecards UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewShopTimecardsUI = function() {
        return isTimecardUser();
    };

    /**
     * User can VIEW Fulfillment CSV Export
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewFulfillmentCsvExport = function() {
        return isInternalOrderAdmin() && isViewWeightUser();
    };

    /**
     * User can VIEW Fulfillment PDF Export
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewFulfillmentPdfExport = function() {
        return isInternalOrderAdmin();
    };

    /**
     * User can VIEW Full Menu
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewFullTrackerMenu = function() {
        return isTrackerAdminUser();
    };

    /**
     * User can VIEW inventory Admin UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewInventoryAdminUI = function () {
        return isTrackerAdminUser();
    };

    /**
     * User can VIEW Inventory Reports Menu UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewInventoryReportsMenuUI = function () {
        return isTrackerUser();
    };

    /**
     * User can VIEW Billing Detail Report UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewInventoryBillingDetailReportUI = function () {
        return isTrackerUser();
    };

    /**
     * User can VIEW Job Billing Summary Report UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewInventoryJobBillingSummaryReportUI = function () {
        return isTrackerUser();
    };

    /**
     * User can VIEW Inventory Orders
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewInventoryOrdersUI = function () {
        return isTrackerAdminUser();
    };

    /**
     * User can VIEW Job Center UI
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewJobCenter = function() {
        return isJobAdminUser();
    };

    /**
     * User can VIEW Jobs Admin
     *
     * @deprecated To standardize naming conventions. Use "canViewJobsAdminUI"
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewJobsAdmin = function () {
        return isJobAdminUser();
    };

    /**
     * User can VIEW Jobs Admin UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewJobsAdminUI = function () {
        return isJobAdminUser();
    };

    /**
     * User can VIEW Job Codes Admin
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewJobCodesAdminUI = function () {
        return isJobAdminUser();
    };

    /**
     * User can VIEW Jobs Summary
     *
     * @deprecated Old Dashboard Permission
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewJobsSummary = function () {
        return isJobAdminUser();
    };

    /**
     * User can VIEW Joints List
     *
     * @since 2.24.0
     * @returns {Boolean}
     */
    helper.canViewJoints = function () {
        return isProductAdminUser();
    };

    /**
     * User can VIEW Landing Page UI
     *
     * @since 3.1.0
     * @returns {Boolean}
     */
    helper.canViewLandingPageUI = function () {
        return true; // All users should have access to the landing page
    };

    /**
     * User can VIEW Logout
     *
     * @since 2.2.0
     * @returns {Boolean}
     */
    helper.canViewLogout = function () {
        return true;
    };

    /**
     * User can VIEW Lookahead Orders
     *
     * @deprecated To standardize naming conventions. Use "canViewLookaheadOrdersUI"
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewLookaheadOrders = function() {
        return isLookaheadOrderAdminUser();
    };

    /**
     * User can VIEW Lookahead Orders UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewLookaheadOrdersUI = function() {
        return isLookaheadOrderAdminUser();
    };

    /**
     * User can VIEW Ordering UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewOrderingUI = function() {
        return isCustomerUser();
    };

    /**
     * User can VIEW Orders UI
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewOrders = function() {
        return !(isTimecardAdmin());
    };

    /**
     * User can VIEW order admin
     *
     * @since 2.2.0
     * @returns {Boolean}
     */
    helper.canViewOrdersAdmin = function () {
        return isCustomerUser() && isOrderAdminUser();
    };

    /**
     * User can VIEW Organizations Admin
     *
     * @deprecated To standardize naming conventions. Use "canViewOrganizationsAdminUI"
     * @since 2.14.0
     * @returns {Boolean}
     */
    helper.canViewOrganizationsAdmin = function () {

        // ecommerce users can NOT see the pricing adjustments
        // see DEPLOYM-3238, ORGUI-94, and LIBENV-174
        return isDivisionAdmin() && !isEcommerceUser();
    };

    /**
     * User can VIEW Organizations Admin UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewOrganizationsAdminUI = function () {

        // ecommerce users can NOT see the pricing adjustments
        // see DEPLOYM-3238, ORGUI-94, and LIBENV-174
        return isDivisionAdmin() && !isEcommerceUser();
    };

    /**
     * User can VIEW Price Adjustments Admin
     *
     * @deprecated To standardize naming conventions. Use "canViewPriceAdjustmentsAdminUI"
     * @since 2.15.0
     * @returns {Boolean}
     */
    helper.canViewPriceAdjustmentsAdmin = function () {

        // ecommerce users can NOT see organizations
        // see DEPLOYM-3238, ORGUI-94, and LIBENV-172
        return isDivisionAdmin() && !isEcommerceUser();
    };

    /**
     * User can VIEW Price Adjustments Admin UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewPriceAdjustmentsAdminUI = function () {

        // ecommerce users can NOT see organizations
        // see DEPLOYM-3238, ORGUI-94, and LIBENV-172
        return isDivisionAdmin() && !isEcommerceUser();
    };

    /**
     * User can VIEW Pricing
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewPricing = function () {
        return isViewPricingUser();
    };

    /**
     * User can VIEW products
     *
     * @since 2.2.0
     * @returns {Boolean}
     */
    helper.canViewProducts = function () {
        return isCustomerUser();
    };

    /**
     * User can VIEW Production Calendar
     *
     * @deprecated To standardize naming conventions. Use "canViewProductionCalendarUI"
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewProductionCalendar = function() {
        return isProductionCalendarUser();
    };

    /**
     * User can VIEW Production Calendar UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewProductionCalendarUI = function() {
        return isProductionCalendarUser();
    };

    /**
     * User can VIEW Fabrication Time on Production Calendar
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewProductionCalendarFabricationTime = function () {
        return isViewFabricationTimeUser() || isShopTimecardUser() || isShopTimecardAdmin();
    };

    /**
     * User can VIEW Product UI
     *
     * @since 2.35.0
     * @returns {Boolean}
     */
    helper.canViewProductUI = function() {
        return isCentralProductUser() || isSuperAdminUser() || isDeploymentAdminUser();
    };

    /**
     * User can VIEW Reimbursements User Interface
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canViewReimbursementsUI = function() {
        return hasReimbursementPermissions();
    };

    /**
     * User can VIEW OWN Crews
     *
     * @since 2.5.0
     * @returns {Boolean}
     */
    helper.canViewOwnCrews = function() {
        return isCrewUser() || isTimecardUser() || hasReimbursementPermissions();
    };

    /**
     * User can VIEW OWN Reimbursements
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canViewOwnReimbursements = function() {
        return hasReimbursementPermissions();
    };

    /**
     * User can VIEW Activity Report
     *
     * @deprecated To standardize naming conventions. Use "canViewActivityReportUI"
     * @since 2.36.0
     * @returns {Boolean}
     */
    helper.canViewActivityReport = function() {
        return isDeploymentAdminUser() || isSuperAdminUser();
    };

    /**
     * User can VIEW Activity Report UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewActivityReportUI = function() {
        return isDeploymentAdminUser() || isSuperAdminUser();
    };

    /**
     * User can VIEW ALL Crews
     *
     * @since 2.5.0
     * @returns {Boolean}
     */
    helper.canViewAllCrews = function() {
        return isCrewUser() || isCrewAdmin();
    };

    /**
     * User can VIEW ALL Reimbursements
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canViewAllReimbursements = function() {
        return isReimbursementAdmin() || isReimbursementForeman();
    };

    /**
     * User can VIEW OWN Reimbursement Entries
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canViewOwnReimbursementEntries = function() {
        return hasReimbursementPermissions();
    };

    /**
     * User can VIEW ALL Reimbursement Entries
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canViewAllReimbursementEntries = function() {
        return isReimbursementAdmin() || isReimbursementForeman();
    };

    /**
     * User can VIEW Application Settings Admin
     *
     * @deprecated To standardize naming conventions. Use "canViewApplicationSettingsAdminUI"
     * @since 2.19.0
     * @returns {Boolean}
     */
    // helper.canViewApplicationSettingsAdmin = function () {
    //     return isDeploymentAdminUser();
    // };

    /**
     * User can VIEW Application Settings Admin UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewApplicationSettingsAdminUI = function () {
        return isSuperAdminUser() || isProductAdminUser() || isDeploymentAdminUser();
    };

    /**
     * User can VIEW Manufacturing Admin UI
     *
     * @deprecated To standardize naming conventions. Use "canViewManufacturingAdminUI"
     * @since 2.28.0
     * @returns {Boolean}
     */
    helper.canViewManufacturingAdmin = function () {
        return helper.canViewManufacturingAdminUI();
    };

    /**
     * User can VIEW Manufacturing Admin UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewManufacturingAdminUI = function () {
        return isSuperAdminUser() || isProductAdminUser() || isDeploymentAdminUser();
    };

    /**
     * User can VIEW Application Settings Admin in Manufacturing Admin UI
     *
     * @deprecated Use "canViewApplicationSettingsAdminUI"
     *
     * @since 3.5.0
     * @returns {Boolean}
     */
    // helper.canViewManufacturingApplicationSettingsAdminUI = function () {
    //     return isSuperAdminUser();
    // };

    /**
     * User can VIEW Reimbursement Reports
     *
     * @deprecated To standardize naming conventions. Use "canViewReimbursementEntriesReportUI"
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canViewReimbursementReports = function() {
        return isReimbursementAdmin();
    };

    /**
     * User can VIEW Reimbursement Entries Report UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewReimbursementEntriesReportUI = function() {
        return isReimbursementAdmin();
    };

    /**
     * User can VIEW saved orders
     *
     * @deprecated To standardize naming conventions. Use "canViewSavedWorkOrdersUI"
     * @since 2.2.0
     * @returns {Boolean}
     */
    helper.canViewSavedOrders = function () {
        return isCustomerUser();
    };

    /**
     * User can VIEW saved work orders
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewSavedWorkOrdersUI = function () {
        return isCustomerUser();
    };

    /**
     * User can VIEW seat users data
     *
     * @since 2.54.0
     * @return {Boolean}
     */
    helper.canViewSeatUsers = function() {
        return isUserAdmin() || isFieldTimecardForeman() || isSuperAdminUser();
    };

    /**
     * User can VIEW Shipping Calendar
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewShippingCalendarUI = function() {
        return isDeliveryCalendarUser();
    };

    /**
     * User can VIEW Shop Timecard Admin
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewShopTimecardAdmin = function() {
        return isShopTimecardAdmin();
    };

    /**
     * User can VIEW Shop Timecard Workstation
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewShopTimecardWorkstation = function() {
        return isShopTimecardUser();
    };

    /**
     * User can VIEW Attendance
     *
     * @since 1.18.0
     * @returns {Boolean}
     */
    helper.canViewTimecardAttendance = function() {
        return isTimecardUser();
    };

    /**
     * User can VIEW Timecards User Interface
     *
     * @since 1.14.0
     * @returns {Boolean}
     */
    helper.canViewTimecardsUI = function() {
        return isTimecardUser();
    };

    /**
     * User can VIEW Timecards
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewTimecards = function() {
        return isTimecardUser();
    };

    /**
     * User can VIEW Timecard Admin Menu
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewTimecardAdminMenu = function() {
        return isShopTimecardAdmin();
    };

    /**
     * User can VIEW Timecard Entries
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewTimecardEntries = function() {
        return isTimecardUser();
    };

    /**
     * User can View Timecard Entries Report
     *
     * @deprecated To standardize naming conventions. Use "canViewTimecardEntriesReportUI"
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewTimecardEntriesReport = function() {
        return isShopTimecardAdmin();
    };

    /**
     * User can View Timecard Entries Report UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewTimecardEntriesReportUI = function() {
        return isShopTimecardAdmin();
    };

    /**
     * User can VIEW Timecard Notifications
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewTimecardNotifications = function() {
        return isShopTimecardAdmin();
    };

    /**
     * User can VIEW Timecards Overview
     *
     * @deprecated To standardize naming conventions. Use "canViewTimecardOverviewReportUI"
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewTimecardsOverview = function() {
        return isTimecardAdmin();
    };

    /**
     * User can VIEW Timecards Overview Report UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewTimecardOverviewReportUI = function() {
        return isTimecardAdmin();
    };

    /**
     * User can VIEW Timecard Users
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewTimecardUsers = function() {
        return isShopTimecardAdmin();
    };

    /**
     * User can VIEW Timecard User Groups
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewTimecardUserGroups = function() {
        return isShopTimecardAdmin();
    };

    /**
     * User can VIEW Tools Admin
     *
     * @deprecated To standardize naming conventions. Use "canViewInventoryAdminUI"
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewToolsAdmin = function () {
        return isTrackerAdminUser();
    };

    /**
     * User can VIEW Tools Summary
     *
     * @deprecated Old Dashboard Permission
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewToolsSummary = function () {
        return isTrackerUser();
    };

    /**
     * User can VIEW Users Admin
     *
     * @deprecated To standardize naming conventions. Use "canViewUsersAdminUI"
     * @since 2.2.0
     * @returns {Boolean}
     */
    helper.canViewUsersAdmin = function () {
        return isUserAdmin() || isDivisionAdmin() || isDeploymentAdminUser();
    };

    /**
     * User can VIEW Users Admin UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewUsersAdminUI = function () {
        return isUserAdmin() || isDivisionAdmin() || isDeploymentAdminUser() || isSuperAdminUser();
    };

    /**
     * User can VIEW User Groups Admin
     *
     * @deprecated To standardize naming conventions. Use "canViewUserGroupsAdminUI"
     * @since 2.3.0
     * @returns {Boolean}
     */
    helper.canViewUserGroupsAdmin = function () {
        return isUserGroupAdmin();
    };

    /**
     * User can VIEW User Groups Admin UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewUserGroupsAdminUI = function () {
        return isUserGroupAdmin();
    };

    /**
     * User can VIEW vehicle assignments
     *
     * @since 3.4.0
     * @returns {Boolean}
     */
    helper.canViewVehicleAssignments = function () {
        return isOrderAdminUser();
    };

    /**
     * User can VIEW Weight
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewWeight = function () {
        return isViewWeightUser();
    };

    /**
     * User can VIEW Work Order Admin
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewWorkOrderAdmin = function() {
        return isInternalOrderAdminDeploymentUser();
    };

    /**
     * User can VIEW Work Orders
     *
     * @deprecated To standardize naming conventions. Use "canViewWorkOrdersUI"
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewWorkOrders = function() {
        return isOrderUser();
    };

    /**
     * User can VIEW Work Orders UI
     *
     * @since 2.54.0
     * @returns {Boolean}
     */
    helper.canViewWorkOrdersUI = function() {
        return isOrderUser();
    };

    /**
     * User can VIEW Work Order Costing Report
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewWorkOrderCostingReport = function() {
        return isInternalOrderAdmin() && isViewPricingUser();
    };

    /**
     * User can VIEW Work Order Invoicing Report
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewWorkOrderInvoicingReport = function() {
        return isInternalOrderAdmin() && isViewPricingUser();
    };

    /**
     * User can VIEW Work Order Shop Report
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canViewWorkOrderShopReport = function() {
        return isInternalOrderAdmin() && isViewFabricationTimeUser();
    };

    /**
     * User can VIEW Work Order Shop Report Modern
     *
     * @since 2.23.0
     * @returns {Boolean}
     */
    helper.canViewWorkOrderShopReportModern = function() {
        return isInternalOrderAdmin() && isViewFabricationTimeUser();
    };

    /**
     * User can VIEW Work Order Billing Report
     *
     * @since 2.23.0
     * @returns {Boolean}
     */
    helper.canViewWorkOrderBillingReport = function() {
        return isInternalOrderAdmin() && isViewPricingUser();
    };

    /**
     * User can VIEW Work Order Supply Only Report
     *
     * @since 2.23.0
     * @returns {Boolean}
     */
    helper.canViewWorkOrderSupplyOnlyReport = function() {
        return isInternalOrderAdmin() && isViewPricingUser();
    };

    /**
     * User can USER Shop Standards
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    helper.canUseShopStandards = function () {
        return isShopStandardsUser();
    };

    /****************************************** Initialization ********************************************************/
    /**
     * Set Permissions
     *
     * @since 1.0.0
     */
    helper.setPermissions = function() {

        FIELD_TIMECARD_WORKSTATION_USER = 1;
        CUSTOMER = 2;
        USER_ADMIN = 4;
        PRODUCT_ADMIN = 8;
        VIEW_PRICING = 16;
        FITTING_ADMIN = 32;
        ORDER_ADMIN = 64;
        DEPLOYMENT_ADMIN = 128;
        DIVISION_ADMIN = 256; // a.k.a Organization Admin
        SUPER_ADMIN = 512;
        FIELD_TIMECARD_ADMIN = 1024;
        // ORDER_EXPORT = 2048; (Currently Unused Here)
        DEVELOPER = 4096;
        JOB_ADMIN = 8192;
        VIEW_WEIGHT = 16384;
        TRACKER_USER = 32768;
        TRACKER_ADMIN = 65536;
        SHOP_TIMECARD_WORKSTATION_USER = 131072;
        SHOP_TIMECARD_ADMIN = 262144;
        SHOP_TIMECARD_VIEW_PRODUCTION_CALENDAR = 524288;
        SHOP_TIMECARD_VIEW_DELIVERY_CALENDAR = 1048576;
        SHOP_TIMECARD_CHANGE_FULFILLMENT_STATUS = 2097152;
        FIELD_TIMECARD_FOREMAN = 4194304;
        LOOKAHEAD_ORDER_ADMIN = 8388608;
        // PUBLIC_API_USER = 16777216; (Currently Unused Here)
        REIMBURSEMENT_USER = 33554432;
        REIMBURSEMENT_ADMIN = 67108864;
        REIMBURSEMENT_FOREMAN = 134217728;
        CREW_USER = 268435456;
        CREW_ADMIN = 536870912;
        TIMECARD_PAYROLL_ADMIN = 1073741824;
        ON_SCREEN_ASSEMBLY_USER = 2147483648;
        FIELD_ORDER_ADMIN = 8589934592;
        CENTRAL_PRODUCT_USER = 17179869184;
    };

    /***************************************** Private Functions ******************************************************/

    /**
     * User can update order.  This is "global" check applied to all order update operations.
     * Add any roles here that are not allowed to update orders.
     *
     * @since 2.40.0
     * @returns {Boolean}
     */
    function canUpdateOrders() {
        return !isFieldOrderAdmin();
    }

    /**
     * User can VIEW Price or Weight
     *
     * @since 3.6.0
     * @returns {Boolean}
     */
    function canViewPriceOrWeight() {
        return isViewPricingUser() || isViewWeightUser();
    }

    /**
     * Has Reimbursement User Permissions
     *
     * @since 2.7.0
     * @returns {Boolean}
     */
    function hasReimbursementPermissions() {
        return isReimbursementUser() || isReimbursementForeman() || isReimbursementAdmin();
    }

    /**
     * Is Central Product User
     *
     * @since 2.45.0
     * @returns {Boolean}
     */
    function isCentralProductUser() {
        return user.hasRole(CENTRAL_PRODUCT_USER);
    }

    /**
     * Is User Who Can Change Fulfillment Status
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isChangeFulfillmentStatusUser() {
        return isInternalOrderAdminDeploymentUser() || user.hasRole(SHOP_TIMECARD_CHANGE_FULFILLMENT_STATUS);
    }

    /**
     * Is Crew Admin User
     *
     * @since 2.5.0
     * @returns {Boolean}
     */
    function isCrewAdmin() {
        return user.hasRole(CREW_ADMIN);
    }

    /**
     * Is Crew User
     *
     * @since 2.5.0
     * @returns {Boolean}
     */
    function isCrewUser() {
        return user.hasRole(CREW_USER);
    }

    /**
     * Is Customer User (aka "Limited Order User". User can create orders but not allowed to view price & weight)
     *
     * @since 1.0.0
     * @return {Boolean}
     */
    function isCustomerUser() {
        return user.hasRole(CUSTOMER);
    }

    /**
     * Is Delivery Calendar User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isDeliveryCalendarUser() {
        var hasFieldTimecardPermissions  = isFieldTimecardUser() || isFieldTimecardAdmin() || isFieldTimecardForeman();
        var isInternalOrEcommerceUser = isEcommerceUser() || isInternalUser();

        return hasFieldTimecardPermissions || isInternalOrderAdminDeploymentUser() || user.hasRole(SHOP_TIMECARD_VIEW_DELIVERY_CALENDAR) || isInternalOrEcommerceUser;
    }

    /**
     * Is Deployment Admin User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isDeploymentAdminUser() {
        return user.hasRole(DEPLOYMENT_ADMIN);
    }

    /**
     * Is Developer User
     *
     * @since 1.0.0
     * @return {Boolean}
     */
    function isDeveloperUser() {
        return user.hasRole(DEVELOPER);
    }

    /**
     * Is Division Admin
     *
     * @since 1.0.0
     * @return {Boolean}
     */
    function isDivisionAdmin() {
        return user.hasRole(DIVISION_ADMIN);
    }

    /**
     * Is Environment Admin
     *
     * @since 2.3.0
     * @return {Boolean}
     */
    function isEnvironmentAdmin() {
        return isDeploymentAdminUser();
    }

    /**
     * Is Field Timecard Admin
     *
     * @since 1.0.0
     * @return {Boolean}
     */
    function isFieldTimecardAdmin() {
        return user.hasRole(FIELD_TIMECARD_ADMIN);
    }

    /**
     * Is Field Timecard Foreman
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isFieldTimecardForeman() {
        return user.hasRole(FIELD_TIMECARD_FOREMAN);
    }

    /**
     * Is Field Timecard Workstation User
     *
     * @since 1.0.0
     * @return {Boolean}
     */
    function isFieldTimecardUser() {
        return user.hasRole(FIELD_TIMECARD_WORKSTATION_USER);
    }

    /**
     * Is Fitting Admin
     *
     * @since 1.0.0
     * @return {Boolean}
     */
    function isFittingAdminUser() {
        return user.hasRole(FITTING_ADMIN);
    }

    /**
     * Is Internal User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isInternalUser() {
        return user.isInternal();
    }

    /**
     * Is Ecommerce User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isEcommerceUser() {
        return user.isEcommerce();
    }

    /**
     * Is Field Order Admin
     *
     * @since 2.40.0
     * @return {Boolean}
     */
    function isFieldOrderAdmin() {
        return user.hasRole(FIELD_ORDER_ADMIN);
    }

    /**
     * Is Lookahead Order Admin User and in an internal group
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isInternalLookaheadOrderAdminUser() {
        return isLookaheadOrderAdminUser() && isInternalUser();
    }

    /**
     * Is Internal && Is Order Admin
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isInternalOrderAdmin() {
        return isOrderAdminUser() && isInternalUser();
    }

    /**
     * Is Internal && Is Order Admin || is Deployment Admin User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isInternalOrderAdminDeploymentUser() {
        return (isInternalUser() && isOrderAdminUser()) || isDeploymentAdminUser();
    }

    /**
     * Is Job Admin User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isJobAdminUser() {
        return user.hasRole(JOB_ADMIN);
    }

    /**
     * Is Lookahead Order Admin User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isLookaheadOrderAdminUser() {
        return user.hasRole(LOOKAHEAD_ORDER_ADMIN);
    }

    /**
     * Is On Screen Assembly User
     *
     * @since 2.20.0
     * @returns {Boolean}
     */
    function isOnScreenAssemblyUser() {
        return user.hasRole(ON_SCREEN_ASSEMBLY_USER);
    }

    /**
     * Is Order Admin User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isOrderAdminUser() {
        return user.hasRole(ORDER_ADMIN);
    }

    /**
     * Is Order User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isOrderUser() {
        return true;
    }

    /**
     * Is Product Admin User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isProductAdminUser() {
        return user.hasRole(PRODUCT_ADMIN);
    }

    /**
     * Is Production Calendar User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isProductionCalendarUser() {
        var hasFieldTimecardPermissions  = isFieldTimecardUser() || isFieldTimecardAdmin() || isFieldTimecardForeman();
        return hasFieldTimecardPermissions || isInternalOrderAdminDeploymentUser() || user.hasRole(SHOP_TIMECARD_VIEW_PRODUCTION_CALENDAR);
    }

    /**
     * Is Reimbursement Admin
     *
     * @since 1.13.0
     * @returns {Boolean}
     */
    function isReimbursementAdmin() {
        return user.hasRole(REIMBURSEMENT_ADMIN);
    }

    /**
     * Is Reimbursement Foreman
     *
     * @since 2.4.0
     * @returns {Boolean}
     */
    function isReimbursementForeman() {
        return user.hasRole(REIMBURSEMENT_FOREMAN);
    }

    /**
     * Is Reimbursement User
     *
     * @since 1.13.0
     * @returns {Boolean}
     */
    function isReimbursementUser() {
        return user.hasRole(REIMBURSEMENT_USER);
    }

    /**
     * Is Shop Timecard Admin
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isShopTimecardAdmin() {
        return user.hasRole(SHOP_TIMECARD_ADMIN);
    }

    /**
     * Is Shop Timecard User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isShopTimecardUser() {
        return user.hasRole(SHOP_TIMECARD_WORKSTATION_USER);
    }

    /**
     * Is Shop Standards User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isShopStandardsUser() {
        return user.getShopStandardsFlag() === 1;
    }

    /**
     * Is Super Admin User
     *
     * @since 1.0.0
     * @return {Boolean}
     */
    function isSuperAdminUser() {
        return user.hasRole(SUPER_ADMIN);
    }

    /**
     * Is Timecard Admin
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isTimecardAdmin() {
        return isShopTimecardAdmin() || isFieldTimecardAdmin() || isFieldTimecardForeman();
    }

    /**
     * Is Timecard Payroll Admin
     *
     * @since 2.19.0
     * @returns {Boolean}
     */
    function isTimecardPayrollAdmin() {
        return user.hasRole(TIMECARD_PAYROLL_ADMIN);
    }

    /**
     * Is Timecard User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isTimecardUser() {
        return isTimecardAdmin() || isShopTimecardUser() || isFieldTimecardUser();
    }

    /**
     * Is Tracker Admin User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isTrackerAdminUser() {
        return user.hasRole(TRACKER_ADMIN);
    }

    /**
     * Is Tracker User
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isTrackerUser() {
        return user.hasRole(TRACKER_USER);
    }

    /**
     * Is User Admin
     *
     * @since 1.0.0
     * @return {Boolean}
     */
    function isUserAdmin() {
        return user.hasRole(USER_ADMIN);
    }

    /**
     * Is User Group Admin
     *
     * @since 2.3.0
     * @return {Boolean}
     */
    function isUserGroupAdmin() {
        return isDeploymentAdminUser()
    }

    /**
     * Is User Who Can View Fabrication Time
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isViewFabricationTimeUser() {
        return isInternalOrderAdmin() || isInternalLookaheadOrderAdminUser();
    }

    /**
     * Is User Who Can View Pricing
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isViewPricingUser() {
        return user.hasRole(VIEW_PRICING);
    }

    /**
     * Is User Who Can View Weight
     *
     * @since 1.0.0
     * @returns {Boolean}
     */
    function isViewWeightUser() {
        return user.hasRole(VIEW_WEIGHT);
    }


    return helper;
};
globalThis.WebductPermissions = WebductPermissions;
// EOF
